<!-- ═══════════════════════════════════════════════════════════════
     MODAL MODE: Self-contained Bootstrap modal with hero banner.
     The modal div MUST be the first child element (no @if wrapper)
     because showAsModal reads children[0] before change detection.
     Bootstrap's .modal class has display:none so this is hidden in panel mode.
     ═══════════════════════════════════════════════════════════════ -->
<div class="modal fade in" tabindex="-1" role="dialog" aria-hidden="true"
    [hidden]="detailsType !== 'modal'">
    <div class="modal-dialog modal-dialog-scrollable" [ngClass]="{
            'modal-lg': modalSize === 'medium',
            'modal-xl': modalSize === 'large',
            'modal-fullscreen': modalSize === 'fullscreen'
        }" role="document">
        <div class="modal-content">

            <!-- Hero Banner -->
            <div class="feature-hero-banner" [style.background]="heroGradient">
                <div class="hero-dot-overlay"></div>

                <!-- Close button (top-right) -->
                <button type="button" class="hero-close-btn" aria-label="Close" (click)="close()">
                    <i class="fas fa-times"></i>
                </button>

                <!-- Centered icon -->
                <div class="hero-icon-circle">
                    <i [class]="typeConfig.icon + ' hero-icon'"></i>
                </div>
            </div>

            <!-- Modal Body -->
            <div class="modal-body modal-details overflow-auto">
                <!-- Title -->
                <div class="mf-title">{{ feature?.name }}</div>

                <!-- Subtitle: Scene · Last Changed -->
                <div class="mf-subtitle">
                    @if (sceneName) {
                        <span>{{ sceneName }}</span>
                    }
                    @if (featureLastChanged) {
                        @if (sceneName) {
                            <span class="mf-sep">&middot;</span>
                        }
                        <span>{{ featureLastChanged }}</span>
                    }
                </div>

                <!-- Loading spinner -->
                @if (retrievingCustomFields) {
                    <div class="d-flex justify-content-center p-4">
                        @for (i of [1,2,3,4,5]; track $index) {
                            <div class="spinner-grow text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        }
                    </div>
                }

                @if (!retrievingCustomFields) {
                    <!-- S8: All empty -->
                    @if (allEmpty) {
                        <div class="all-empty-message">
                            <i [class]="typeConfig.icon + ' all-empty-icon'"></i>
                            <div class="all-empty-title">No details yet</div>
                            <div class="all-empty-hint">This feature hasn't been enriched with a description, fields, or attachments.</div>
                        </div>
                    }

                    <!-- Normal states (S1–S7) -->
                    @if (!allEmpty) {
                        <!-- Description (always shown) -->
                        <div class="mf-section">
                            <div class="mf-section-title">Description</div>
                            @if (isDescriptionSet) {
                                <div [innerHTML]="sanitizedMessage"
                                    class="mf-desc ck-content"></div>
                            }
                            @if (!isDescriptionSet) {
                                <div class="empty-block">
                                    <i class="fas fa-align-left empty-block-icon"></i>
                                    <div>No description</div>
                                </div>
                            }
                        </div>

                        <!-- Custom Fields (only if present) -->
                        @if (hasCustomFields) {
                            <div class="mf-section">
                                <div class="mf-section-title">Custom Fields</div>
                                <div class="fields-table">
                                    @for (field of customFields; track field.key) {
                                        <div class="field-row">
                                            <div class="field-label">{{ field.key }}</div>
                                            <div class="field-value">{{ field.value }}</div>
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                        <!-- Attachments (only if present) -->
                        @if (hasAttachments) {
                            <div class="mf-section">
                                <div class="mf-section-title">Attachments</div>
                                <shared-view-attachment-list [attachments]="attachments"></shared-view-attachment-list>
                            </div>
                        }
                    }
                }
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" (click)="close()">Close</button>
            </div>

        </div>
    </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════
     PANEL MODE: Clean sectioned layout for right sidebar
     ═══════════════════════════════════════════════════════════════ -->
@if (detailsType === 'panel') {

    <!-- Loading spinner -->
    @if (retrievingCustomFields) {
        <div class="detail-content-area d-flex justify-content-center p-4">
            @for (i of [1,2,3,4,5]; track $index) {
                <div class="spinner-grow text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            }
        </div>
    }

    @if (!retrievingCustomFields) {

        <!-- S8: All empty -->
        @if (allEmpty) {
            <div class="detail-content-area">
                <div class="detail-section">
                    <div class="detail-section-title">Description</div>
                    <div class="empty-block">
                        <i class="fas fa-align-left empty-block-icon"></i>
                        <div>No description</div>
                    </div>
                </div>
                <div class="all-empty-message">
                    <i [class]="typeConfig.icon + ' all-empty-icon'"></i>
                    <div class="all-empty-title">No additional details</div>
                    <div class="all-empty-hint">Add a description, custom fields, or files to enrich this feature.</div>
                </div>
            </div>
        }

        <!-- Normal states (S1–S7) -->
        @if (!allEmpty) {
            <div class="detail-content-area">
                <!-- Description (always shown) -->
                <div class="detail-section">
                    <div class="detail-section-title">Description</div>
                    @if (isDescriptionSet) {
                        <div [innerHTML]="sanitizedMessage"
                            class="detail-description ck-content"></div>
                    }
                    @if (!isDescriptionSet) {
                        <div class="empty-block">
                            <i class="fas fa-align-left empty-block-icon"></i>
                            <div>No description</div>
                        </div>
                    }
                </div>

                <!-- Custom Fields (only if present) -->
                @if (hasCustomFields) {
                    <div class="detail-section">
                        <div class="detail-section-title">Custom Fields</div>
                        <div class="fields-table">
                            @for (field of customFields; track field.key) {
                                <div class="field-row">
                                    <div class="field-label">{{ field.key }}</div>
                                    <div class="field-value">{{ field.value }}</div>
                                </div>
                            }
                        </div>
                    </div>
                }

                <!-- Attachments (only if present) -->
                @if (hasAttachments) {
                    <div class="detail-section">
                        <div class="detail-section-title">Attachments</div>
                        <shared-view-attachment-list [attachments]="attachments"></shared-view-attachment-list>
                    </div>
                }
            </div>
        }

    }
}
